import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, ShieldCheck, Building, Save } from 'lucide-react';
import { api, extractApiError } from '@/lib/api';
import { toast } from '@/stores/toast';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { formatDate } from '@/lib/utils';

export function PlatformTenantDetailPage() {
  const { id } = useParams();
  const qc = useQueryClient();

  const detail = useQuery({
    queryKey: ['platform-admin', 'tenant', id],
    queryFn: async () => (await api.get(`/api/platform-admin/tenants/${id}`)).data,
  });

  const users = useQuery({
    queryKey: ['platform-admin', 'tenant-users', id],
    queryFn: async () => (await api.get(`/api/platform-admin/tenants/${id}/users`)).data,
  });

  const update = useMutation({
    mutationFn: async (body: Record<string, unknown>) =>
      (await api.patch(`/api/platform-admin/tenants/${id}`, body)).data,
    onSuccess: () => {
      toast.success('Tenant updated');
      qc.invalidateQueries({ queryKey: ['platform-admin', 'tenant', id] });
    },
    onError: (err) => toast.error('Update failed', extractApiError(err).message),
  });

  const [maxSeats, setMaxSeats] = useState<number | ''>('');
  const [plan, setPlan] = useState<string>('');

  if (detail.isLoading) {
    return <Skeleton className="h-64" />;
  }
  if (!detail.data) return <div>Not found</div>;

  const t = detail.data.tenant;
  const sub = detail.data.subscription;
  const usage = detail.data.usage30d;

  return (
    <div className="space-y-6">
      <div>
        <Button asChild variant="ghost" size="sm" className="-ml-2 mb-2">
          <Link to="/platform-admin/tenants">
            <ArrowLeft className="mr-2 h-4 w-4" /> Tenants
          </Link>
        </Button>
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <div className="flex h-10 w-10 items-center justify-center rounded-md bg-primary/10 text-primary">
                <Building className="h-5 w-5" />
              </div>
              <div>
                <h1 className="font-display text-2xl">{t.name}</h1>
                <div className="text-xs text-muted-foreground">
                  <code>{t.slug}</code> · created {formatDate(t.createdAt)}
                </div>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant={t.isActive ? 'success' : 'destructive'}>{t.isActive ? 'Active' : 'Inactive'}</Badge>
            {t.gxpValidated && (
              <Badge variant="secondary">
                <ShieldCheck className="mr-1 h-3 w-3" /> GxP
              </Badge>
            )}
          </div>
        </div>
      </div>

      {/* Key metrics */}
      <div className="grid gap-3 md:grid-cols-4">
        <Mini label="Users" value={t._count.users} />
        <Mini label="Documents" value={t._count.documents} />
        <Mini label="Workflow runs" value={t._count.workflowRuns} />
        <Mini label="PV cases" value={t._count.pvCases} />
      </div>

      <div className="grid gap-4 lg:grid-cols-[1fr,1.4fr]">
        {/* Editable settings */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Plan & capacity</CardTitle>
            <CardDescription>Platform-level overrides.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <Label>Plan</Label>
              <select
                defaultValue={t.plan.toLowerCase()}
                onChange={(e) => setPlan(e.target.value)}
                className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
              >
                <option value="starter">Starter</option>
                <option value="professional">Professional</option>
                <option value="enterprise">Enterprise</option>
              </select>
            </div>
            <div>
              <Label>Max seats</Label>
              <Input
                type="number"
                defaultValue={t.maxSeats}
                onChange={(e) => setMaxSeats(Number(e.target.value))}
                min={1}
              />
            </div>
            <div className="flex items-center justify-between rounded-md border p-3">
              <div className="text-sm">
                <div className="font-medium">GxP validated</div>
                <div className="text-xs text-muted-foreground">Enables production workflows.</div>
              </div>
              <input
                type="checkbox"
                defaultChecked={t.gxpValidated}
                onChange={(e) => update.mutate({ gxpValidated: e.target.checked })}
                className="h-4 w-4 accent-primary"
              />
            </div>
            <div className="flex items-center justify-between rounded-md border p-3">
              <div className="text-sm">
                <div className="font-medium">Active</div>
                <div className="text-xs text-muted-foreground">Deactivating locks all tenant logins.</div>
              </div>
              <input
                type="checkbox"
                defaultChecked={t.isActive}
                onChange={(e) => update.mutate({ isActive: e.target.checked })}
                className="h-4 w-4 accent-primary"
              />
            </div>
            <Button
              onClick={() => {
                const body: Record<string, unknown> = {};
                if (plan && plan !== t.plan.toLowerCase()) body.plan = plan;
                if (maxSeats !== '' && maxSeats !== t.maxSeats) body.maxSeats = maxSeats;
                if (Object.keys(body).length === 0) {
                  toast.info('No changes to save');
                  return;
                }
                update.mutate(body);
              }}
              disabled={update.isPending}
            >
              <Save className="mr-1.5 h-3.5 w-3.5" /> Save plan + seats
            </Button>
          </CardContent>
        </Card>

        {/* Billing summary */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Billing snapshot</CardTitle>
            <CardDescription>Region {t.billingRegion} · {t.billingCurrency}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {sub ? (
              <div className="rounded-md border p-3 text-sm">
                <div className="flex items-center justify-between">
                  <div className="font-medium">
                    {sub.plan.name} · {sub.billingPeriod}
                  </div>
                  <Badge variant={sub.status === 'active' ? 'success' : sub.status === 'past_due' ? 'destructive' : 'warning'}>
                    {sub.status}
                  </Badge>
                </div>
                <div className="mt-1 text-xs text-muted-foreground">
                  Period: {formatDate(sub.currentPeriodStart)} to {formatDate(sub.currentPeriodEnd)}
                </div>
                {sub.stripeSubscriptionId && (
                  <div className="mt-0.5 text-[10px] text-muted-foreground">
                    Stripe sub: <code>{sub.stripeSubscriptionId}</code>
                  </div>
                )}
              </div>
            ) : (
              <div className="rounded-md border border-dashed p-3 text-sm text-muted-foreground">
                No active subscription. Tenant is on a pilot / comp plan.
              </div>
            )}
            <div className="grid grid-cols-3 gap-3 rounded-md border p-3 text-center">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">30d pages</div>
                <div className="font-display text-lg">{(usage?.pages ?? 0).toLocaleString()}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">30d AI cost</div>
                <div className="font-display text-lg">${(usage?.aiCostUsd ?? 0).toFixed(2)}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">30d events</div>
                <div className="font-display text-lg">{(usage?.events ?? 0).toLocaleString()}</div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Users */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Users ({users.data?.total ?? 0})</CardTitle>
          <CardDescription>All accounts on this tenant.</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {users.isLoading ? (
            <Skeleton className="m-4 h-24" />
          ) : (
            <div className="divide-y">
              {users.data?.items?.map((u: {
                id: string; email: string; firstName: string; lastName: string;
                role: string; isActive: boolean; mfaEnabled: boolean;
                isSuperAdmin: boolean; lastLoginAt: string | null; createdAt: string;
              }) => (
                <div key={u.id} className="flex items-center justify-between gap-4 px-4 py-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{u.firstName} {u.lastName}</span>
                      {u.isSuperAdmin && <Badge variant="secondary">Super</Badge>}
                    </div>
                    <div className="text-xs text-muted-foreground">{u.email}</div>
                  </div>
                  <div className="flex items-center gap-2 text-xs">
                    {u.mfaEnabled ? (
                      <Badge variant="success">MFA</Badge>
                    ) : (
                      <Badge variant="secondary">No MFA</Badge>
                    )}
                    <Badge variant="secondary">{u.role}</Badge>
                    <Badge variant={u.isActive ? 'success' : 'destructive'}>
                      {u.isActive ? 'Active' : 'Inactive'}
                    </Badge>
                    <span className="w-24 text-right text-muted-foreground">
                      {u.lastLoginAt ? formatDate(u.lastLoginAt) : 'never'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function Mini({ label, value }: { label: string; value: number }) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
        <div className="mt-1 font-display text-xl">{value.toLocaleString()}</div>
      </CardContent>
    </Card>
  );
}
